// In-game overlays: kill log, ammo/weapon readout, leaderboard, round badge,
// series board, and the respawn overlay.

import { powerupDef, WEAPON_POWERUPS, type TankDTO } from "../../shared/protocol/index.js";
import { $, escapeHtml, replay } from "../dom.js";
import { roundsToWin, standingHtml } from "./labels.js";
import { renderer, state, TEAM_TINT } from "../state.js";

// ---- Kill / suicide log (icons + colored names + points) ----

function nameSpan(index: number): string {
  const r = state.roster.get(index);
  return `<span class="lg-name" style="color:${r?.color ?? "#888"}">${escapeHtml(r?.name ?? "?")}</span>`;
}

// Entries are appended as nodes (not an innerHTML rebuild) so each row's
// slide-in animation plays exactly once, on arrival.
export function logKillEvent(e: { type: number; killer: number; victim: number; points: number }): void {
  const pts = `<span class="lg-pts ${e.points >= 0 ? "pos" : "neg"}">${e.points >= 0 ? "+" : ""}${e.points}</span>`;
  let html: string;
  if (e.type === 0) html = `${nameSpan(e.killer)} 🎯 ${nameSpan(e.victim)} ${pts}`; // kill
  else if (e.type === 2) html = `${nameSpan(e.killer)} 💀 ${nameSpan(e.victim)} ${pts}`; // team-kill
  else html = `💀 ${nameSpan(e.victim)} ${pts}`; // suicide / self-destruct
  const log = $("killlog");
  const li = document.createElement("li");
  li.innerHTML = html;
  log.appendChild(li);
  while (log.children.length > 6) log.removeChild(log.firstChild!);
}

export function clearKillLog(): void {
  $("killlog").innerHTML = "";
}

/** "Round 2 · first to 3" pill in the game header (hidden for single rounds). */
export function renderRoundBadge(): void {
  const el = $("gh-round");
  const cfg = state.currentLobby?.config;
  // A multi-round match is "first to X"; show the current round + that target
  // rather than a fraction over the (large) worst-case round cap.
  if (state.roundInfo.total > 1 && cfg) {
    el.textContent = `Round ${state.roundInfo.round} · first to ${roundsToWin(cfg)}`;
    el.classList.remove("hidden");
  } else {
    el.classList.add("hidden");
  }
}

/** In-game round scoreboard in the leaderboard sidebar (multi-round matches). */
export function renderSeriesBoard(): void {
  const board = $("series-board");
  if (state.roundInfo.total <= 1 || state.roundStanding.length === 0) {
    board.classList.add("hidden");
    return;
  }
  $("series-board-rows").innerHTML = standingHtml(state.roundStanding);
  board.classList.remove("hidden");
}

// ---- Power-up tray (floating circular icon badges beside the HUD) ----
// 24×24 stroke icons per power-up id; anything without one falls back to the
// registry's text emblem.
const PU_ICONS: Record<string, string> = {
  speed: '<path d="M 4 6 L 10 12 L 4 18 M 10 6 L 16 12 L 10 18 M 16 6 L 22 12 L 16 18" />',
  shield: '<path d="M12 2l8 3v6c0 5-3.5 9-8 11-4.5-2-8-6-8-11V5z" stroke-width="2" /><path d="M12 5.5l5.5 2v4c0 3.5-2.5 6.5-5.5 8-3-1.5-5.5-4.5-5.5-8v-4z" opacity="0.6" stroke-width="1.2" />',
  sniper: '<circle cx="12" cy="12" r="7" stroke-width="2" /><circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" /><path d="M12 2v3 M12 19v3 M2 12h3 M19 12h3 M6 6l2 2 M18 18l2 2 M6 18l2-2 M18 6l2-2" stroke-width="1.5" />',
  explosive: '<path d="M12 2l2.5 5 5.5.5-4 4 1.5 5.5-5.5-3-5.5 3 1.5-5.5-4-4 5.5-.5z" stroke-width="2" /><path d="M12 5.5l1.5 3 3 .5-2.5 2.5 1 3.5-3-2-3 2 1-3.5-2.5-2.5 3-.5z" fill="currentColor" stroke="none" />',
  laser: '<path d="M3 12h18 M6 9v6 M18 9v6" stroke-width="2" /><circle cx="12" cy="12" r="4.5" stroke-width="1.5" /><path d="M12 9v6 M9 12h6" stroke-width="1" />',
  tracking: '<circle cx="12" cy="12" r="9" stroke-width="2" /><circle cx="12" cy="12" r="5" stroke-width="1" opacity="0.6" /><line x1="12" y1="12" x2="19" y2="5" stroke-width="2" /><circle cx="17" cy="7" r="1.5" fill="currentColor" stroke="none" /><circle cx="7" cy="16" r="1" fill="currentColor" stroke="none" opacity="0.5" />',
  multishot: '<path d="M12 20V5 M12 20L5 8 M12 20l7-12 M5 8h14" stroke-width="2" /><circle cx="12" cy="4" r="1.5" fill="currentColor" stroke="none" /><circle cx="5" cy="7" r="1.5" fill="currentColor" stroke="none" /><circle cx="19" cy="7" r="1.5" fill="currentColor" stroke="none" />',
  rapidfire: '<path d="M3 8h6 M11 8h10 M3 16h10 M15 16h6" stroke-width="2.2" /><path d="M7 12h10" stroke-width="3" />',
  scope: '<path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" stroke-width="2" /><circle cx="12" cy="12" r="4.5" stroke-width="1.2" /><circle cx="12" cy="12" r="1.8" fill="currentColor" stroke="none" />',
};

function puOrb(id: string, color: string, label: string, count = 0, cls = "", emblem = "?"): string {
  const icon = PU_ICONS[id];
  const face = icon
    ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${icon}</svg>`
    : `<b class="pu-glyph">${escapeHtml(emblem)}</b>`;
  return (
    `<span class="pu-orb${cls ? ` ${cls}` : ""}" style="--pu-color:${color}" title="${escapeHtml(label)}">` +
    face +
    (count > 1 ? `<i class="pu-n">${count}</i>` : "") +
    `</span>`
  );
}

// Only touch the DOM when the tray content changes: rebuilding innerHTML on
// every 10 Hz HUD tick would restart the charging pulse animation each time.
let lastTrayHtml: string | null = null;

function renderPowerupTray(me: TankDTO): string {
  let html = "";
  if (me.charging) {
    html += puOrb("laser", "#9b3fd6", "charging…", 0, "charging");
  } else {
    for (const w of WEAPON_POWERUPS) {
      const charges = me.weaponCharges[w] ?? 0;
      if (charges > 0) {
        const def = powerupDef(w);
        html += puOrb(def.id, def.color, def.label, charges, "", def.emblem);
      }
    }
  }
  if (me.boosted) {
    const def = powerupDef("speed");
    html += puOrb(def.id, def.color, def.label);
  }
  if (me.shielded) {
    const def = powerupDef("shield");
    html += puOrb(def.id, def.color, def.label);
  }
  if (me.scoped) {
    const def = powerupDef("scope");
    html += puOrb(def.id, def.color, def.label);
  }
  if (html !== lastTrayHtml) {
    lastTrayHtml = html;
    const tray = $("powerup-tray");
    tray.innerHTML = html;
    tray.classList.toggle("hidden", html === "");
  }
  return html;
}

// Rebuild the health/ammo markup only when it changes (this runs at 10 Hz),
// and track the local HP so the strip can flash when damage lands.
let lastHealthHtml: string | null = null;
let lastPipsHtml: string | null = null;
let lastReloadHtml: string | null = null;
let lastHudHp = -1;

export function renderHud(me: TankDTO | undefined): void {
  const strip = $("hud-strip");
  if (!me || !state.inGame || !me.alive) {
    strip.classList.add("hidden");
    lastHudHp = -1;
    if (lastTrayHtml !== "") {
      lastTrayHtml = "";
      $("powerup-tray").classList.add("hidden");
    }
    return;
  }
  // The strip now holds only the health bar, which is hidden entirely on 1-HP
  // games (the default) where it conveys nothing — so hide the whole strip
  // then, otherwise it shows as an empty box that reads like a stray button.
  const healthSection = $("hud-health-section");
  if (me.maxHp <= 1) {
    strip.classList.add("hidden");
    healthSection.classList.add("hidden");
    lastHudHp = me.hp;
  } else {
    strip.classList.remove("hidden");
    healthSection.classList.remove("hidden");

    // Flash the whole strip red for a beat when the local tank takes damage.
    if (lastHudHp > 0 && me.hp < lastHudHp) replay(strip, "hud-hit");
    lastHudHp = me.hp;

    const healthEl = $("hud-health");
    let healthHtml = "";
    for (let i = 0; i < me.maxHp; i++) {
      const isFull = i < me.hp;
      const ratio = me.hp / me.maxHp;
      const colorClass = ratio > 0.5 ? "high" : ratio > 0.25 ? "med" : "low";
      healthHtml += `<span class="health-seg ${isFull ? colorClass : ""}"></span>`;
    }
    if (healthHtml !== lastHealthHtml) {
      lastHealthHtml = healthHtml;
      healthEl.innerHTML = healthHtml;
    }
  }

  // Ammo
  const ammoPipsGroup = $("ammo-pips-group");
  const ammoReloadingEl = $("hud-ammo-reloading");

  const spacing = Math.min(10, 90 / me.maxAmmo);
  const startX = 6;

  let pipsHtml = "";
  for (let i = 0; i < me.maxAmmo; i++) {
    const isSpent = i >= me.ammo;
    const x = startX + i * spacing;
    pipsHtml += `<path d="M ${x} 22 L ${x + 5} 22 L ${x + 5} 10 L ${x + 2.5} 3 L ${x} 10 Z" class="ammo-bullet ${isSpent ? "spent" : ""}" />`;
  }
  if (pipsHtml !== lastPipsHtml) {
    lastPipsHtml = pipsHtml;
    ammoPipsGroup.innerHTML = pipsHtml;
  }

  const reloadHtml = me.reloadIn > 0 ? `<span class="reload">reloading ${Math.ceil(me.reloadIn)}s</span>` : "";
  if (reloadHtml !== lastReloadHtml) {
    lastReloadHtml = reloadHtml;
    ammoReloadingEl.innerHTML = reloadHtml;
  }

  renderPowerupTray(me);

  // Radar — shown only when both the host's game setting and the player's own
  // toggle allow it. Blips are all red (real-radar look), regardless of team.
  const radarSection = $("hud-radar-section");
  const radarOn = (state.currentLobby?.config.radar ?? true) && state.radarEnabled;
  if (!radarOn) {
    radarSection.classList.add("hidden");
  } else {
    radarSection.classList.remove("hidden");
    const radarDotsGroup = $("radar-dots-group");
    let dotsHtml = "";
    const snap = renderer.latest();
    if (snap) {
      const meTank = snap.tanks.find(t => t.id === state.playerId);
      if (meTank) {
        const radarRadius = 600; // units to edge of radar
        for (const t of snap.tanks) {
          if (!t.alive || t.id === state.playerId) continue;
          const dx = t.x - meTank.x;
          const dy = t.y - meTank.y;
          if (Math.hypot(dx, dy) <= radarRadius) {
            // Map [-radarRadius, radarRadius] to [3, 97] to fit within the svg bounds nicely
            const px = ((dx / radarRadius) * 44) + 50;
            const py = ((dy / radarRadius) * 44) + 50;
            dotsHtml += `<circle cx="${px}" cy="${py}" r="5" class="radar-dot" />`;
          }
        }
      }
    }
    radarDotsGroup.innerHTML = dotsHtml;
  }
}

/**
 * One sidebar leaderboard row: medal/rank chip, color swatch, name, metric,
 * and a thin score bar (relative to the leader) tinted with the row's color.
 */
function lbRow(
  i: number,
  color: string,
  name: string,
  metric: string,
  pct: number,
  me: boolean,
  dead = false
): string {
  return (
    `<li class="${me ? "me " : ""}${dead ? "dead" : ""}">` +
    `<span class="rank${i < 3 ? ` r${i + 1}` : ""}">${i + 1}</span>` +
    `<span class="swatch" style="background:${color}"></span>` +
    `<span class="nm">${escapeHtml(name)}</span>` +
    `<span class="pts">${metric}</span>` +
    `<i class="lb-bar" style="width:${pct}%;background:${color}"></i>` +
    `</li>`
  );
}

/** Bar length relative to the leader; zero scores show no bar. */
function lbPct(val: number, max: number): number {
  return val > 0 ? Math.max(5, Math.round((val / Math.max(1, max)) * 100)) : 0;
}

// Rebuild the rows only when their content changes — this runs at 10 Hz.
let lastLbHtml: string | null = null;

/** In-arena leaderboard. Team VS ranks by combined team points; else per-player. */
export function renderLeaderboard(): void {
  const snap = renderer.latest();
  if (!snap) return;

  // Match Info Panel
  const mi = $("match-info");
  if (state.inGame && state.currentLobby) {
    mi.classList.remove("hidden");
    $("mi-round").textContent = `${state.roundInfo.round} / ${state.roundInfo.total}`;
    
    if (state.matchStartTime) {
      const ms = Math.floor((performance.now() - state.matchStartTime) / 1000);
      const m = Math.floor(ms / 60).toString().padStart(2, "0");
      const s = (ms % 60).toString().padStart(2, "0");
      $("mi-time").textContent = `${m}:${s}`;
    }

    const mode = state.currentLobby.config.mode;
    let objText = "Survive";
    if (mode === "ctf") {
      objText = state.currentLobby.config.ctfScoreMode === "deliver" ? "Capture flags" : "Control flags";
    } else if (mode === "teams") {
      objText = "Team Skirmish";
    } else if (mode === "ffa") {
      objText = "Eliminate everyone";
    }
    $("mi-obj").textContent = objText;
  } else {
    mi.classList.add("hidden");
  }

  let html = "";

  // Capture the Flag: rank teams — by flags captured (deliver) or points earned
  // (conquest/carry), live from the tanks in the snapshot.
  if (state.currentLobby?.config.mode === "ctf") {
    const names = state.currentLobby?.teamNames ?? [];
    const colors = state.currentLobby?.teamColors ?? [];
    const sm = state.currentLobby?.config.ctfScoreMode;
    const conquest = sm === "conquest" || sm === "carry";
    const myTeam = snap.tanks.find((t) => t.id === state.playerId)?.team;
    const byTeam = new Map<number, number>();
    const memberN = new Map<number, number>();
    for (const t of snap.tanks) {
      byTeam.set(t.team, (byTeam.get(t.team) ?? 0) + (conquest ? t.score : t.captures));
      memberN.set(t.team, (memberN.get(t.team) ?? 0) + 1);
    }
    const teamsRanked = [...memberN.keys()]
      .map((team) => ({ team, val: byTeam.get(team) ?? 0 }))
      .sort((a, b) => b.val - a.val || a.team - b.team);
    const max = teamsRanked[0]?.val ?? 0;
    teamsRanked.forEach(({ team, val }, i) => {
      const tint = colors[team] ?? TEAM_TINT[team % TEAM_TINT.length];
      const tname = `${names[team] ?? `Team ${team + 1}`} (${memberN.get(team) ?? 0})`;
      html += lbRow(i, tint, tname, conquest ? `${val}` : `⚑ ${val}`, lbPct(val, max), team === myTeam);
    });
  } else if (state.currentLobby?.config.mode === "teams") {
    const totals = new Map<number, { score: number; n: number }>();
    for (const t of snap.tanks) {
      const cur = totals.get(t.team) ?? { score: 0, n: 0 };
      cur.score += t.score;
      cur.n += 1;
      totals.set(t.team, cur);
    }
    const myTeam = snap.tanks.find((t) => t.id === state.playerId)?.team;
    const names = state.currentLobby?.teamNames ?? [];
    const colors = state.currentLobby?.teamColors ?? [];
    const teamsRanked = [...totals.entries()].sort((a, b) => b[1].score - a[1].score || a[0] - b[0]);
    const max = teamsRanked[0]?.[1].score ?? 0;
    teamsRanked.forEach(([team, d], i) => {
      const tint = colors[team] ?? TEAM_TINT[team % TEAM_TINT.length];
      html += lbRow(i, tint, `${names[team] ?? `Team ${team + 1}`} (${d.n})`, `${d.score}`, lbPct(d.score, max), team === myTeam);
    });
  } else {
    // Last Man Standing is about survival, not points — rank by lives remaining
    // (most lives, then who's still alive).
    const lms = state.currentLobby?.config.mode === "lms";
    const ranked = [...snap.tanks].sort((a, b) =>
      lms
        ? b.livesLeft - a.livesLeft || Number(b.alive) - Number(a.alive) || a.name.localeCompare(b.name)
        : b.score - a.score || a.name.localeCompare(b.name)
    );
    const max = lms
      ? Math.max(0, ...ranked.map((t) => t.livesLeft))
      : Math.max(0, ...ranked.map((t) => t.score));
    ranked.forEach((t, i) => {
      const metric = lms ? `♥ ${t.livesLeft}` : `${t.score}`;
      const val = lms ? t.livesLeft : t.score;
      html += lbRow(i, t.color, t.name, metric, lbPct(val, max), t.id === state.playerId, !t.alive);
    });
  }

  if (html !== lastLbHtml) {
    lastLbHtml = html;
    $("leaderboard-rows").innerHTML = html;
  }
}

export function updateRespawnOverlay(me: { alive: boolean; respawnIn: number } | undefined): void {
  const el = $("respawn");
  if (me && !me.alive && !state.matchEndTimeout) {
    el.classList.remove("hidden");
    const countEl = $("respawn-count");
    const next = String(Math.max(0, Math.ceil(me.respawnIn)));
    // Pop the number each time it ticks down.
    if (countEl.textContent !== next) {
      countEl.textContent = next;
      replay(countEl, "tick");
    }
  } else {
    el.classList.add("hidden");
  }
}
